/**
 * Shared building blocks for feature pages (/game-panel, /hardware).
 * Mirrors the design language used by ServicesSection and PanelShowcase:
 * akira headings, poppins body, black base, purple glow + hairline per section.
 *
 * Deliberately NOT a client module: the pages are Server Components and pass
 * icon components in as props, which cannot cross a client boundary. Anything
 * here that needs state lives in its own "use client" file (see FaqItem).
 */

import { type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import type { IconType } from "react-icons";

/** Section shell: top hairline, radial wash, optional faint grid. */
export function FeatureSection({
    id,
    children,
    grid = false,
    className = "",
}: {
    id?: string;
    children: ReactNode;
    grid?: boolean;
    className?: string;
}) {
    return (
        <section id={id} className={`relative z-10 w-full bg-black overflow-hidden px-4 sm:px-8 py-16 sm:py-24 ${className}`}>
            <div className="absolute top-0 inset-x-0 h-[1px] w-full mask-x-from-50%" style={{ backgroundColor: "#6d17b367" }} />
            <div
                className="pointer-events-none absolute inset-0"
                style={{ backgroundImage: "radial-gradient(ellipse at 40% 0px, #7c44e523 0%, transparent 60%, transparent 100%)" }}
            />
            {grid && (
                <svg
                    aria-hidden="true"
                    className="pointer-events-none h-full w-full absolute inset-0 opacity-10 fill-black stroke-purple-400/40"
                    style={{ maskImage: "radial-gradient(ellipse 60% 80% at center, white 30%, transparent 70%)" }}
                >
                    <defs>
                        <pattern id={`fp-grid-${id ?? "x"}`} width="20" height="20" patternUnits="userSpaceOnUse" x="-1" y="-1">
                            <path d="M.5 20V.5H20" fill="none" strokeDasharray="0" />
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" strokeWidth="0" fill={`url(#fp-grid-${id ?? "x"})`} />
                </svg>
            )}
            <div className="relative max-w-6xl mx-auto">{children}</div>
        </section>
    );
}

/** Eyebrow + gradient-accented heading + optional lede. */
export function SectionHeading({
    eyebrow,
    title,
    accent,
    lede,
    center = false,
}: {
    eyebrow?: ReactNode;
    title: string;
    accent?: string;
    lede?: string;
    center?: boolean;
}) {
    return (
        <div className={`max-w-2xl ${center ? "mx-auto text-center" : "text-left"}`}>
            {eyebrow && (
                <span className="text-purple-400/80 font-poppins text-xs tracking-widest uppercase">{eyebrow}</span>
            )}
            <h2 className="font-akira text-2xl sm:text-3xl text-white leading-snug mt-3">
                {title}{" "}
                {accent && (
                    <span
                        className="bg-clip-text text-transparent"
                        style={{ backgroundImage: "linear-gradient(90deg, #a855f7 0%, #7c3aed 100%)" }}
                    >
                        {accent}
                    </span>
                )}
            </h2>
            {lede && (
                <p className="text-gray-500 font-poppins text-sm sm:text-base mt-3 leading-relaxed">{lede}</p>
            )}
        </div>
    );
}

/** Icon + title + copy card, used for benefit and feature grids. */
export function FeatureCard({ Icon, title, desc }: { Icon: IconType; title: string; desc: string }) {
    return (
        <div
            className="rounded-2xl p-5 h-full transition-colors duration-200 hover:border-purple-500/40"
            style={{ background: "#19131f79", border: "1px solid #7c44e511" }}
        >
            <div
                className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                style={{ background: "#7c3aed" }}
            >
                <Icon className="text-white" size={18} />
            </div>
            <p className="text-white text-sm font-semibold font-poppins mb-1.5">{title}</p>
            <p className="text-gray-500 text-xs font-poppins leading-relaxed">{desc}</p>
        </div>
    );
}

/** Numbered step with a connecting rail, for "how it works" sequences. */
export function StepCard({
    step,
    title,
    desc,
    last = false,
}: {
    step: number;
    title: string;
    desc: string;
    last?: boolean;
}) {
    return (
        <div className="relative flex gap-5">
            <div className="flex flex-col items-center">
                <div
                    className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center font-akira text-xs text-white"
                    style={{ background: "#7c3aed", boxShadow: "0 0 0 4px rgba(124,58,237,0.15)" }}
                >
                    {step}
                </div>
                {!last && <div className="w-px flex-1 mt-2 bg-gradient-to-b from-purple-500/40 to-transparent" />}
            </div>
            <div className={last ? "pb-0" : "pb-8"}>
                <p className="text-white text-sm font-semibold font-poppins mb-1.5">{title}</p>
                <p className="text-gray-500 text-xs sm:text-sm font-poppins leading-relaxed max-w-xl">{desc}</p>
            </div>
        </div>
    );
}

/** Collapsible FAQ rows live in FaqItem.tsx — they need client-side state. */

/** Browser-chrome frame around a screenshot. Mirrors PanelShowcase's desktop mock. Omit `url` to hide the address bar. */
export function BrowserFrame({ url, children }: { url?: string; children: ReactNode }) {
    return (
        <div
            className="relative w-full rounded-2xl overflow-hidden border border-white/5"
            style={{
                background: "rgba(255,255,255,0.04)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                boxShadow: "0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)",
            }}
        >
            <div
                className="flex items-center gap-2 px-4 py-3 border-b border-white/5"
                style={{ background: "rgba(255,255,255,0.03)" }}
            >
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
                <div className="ml-3 flex-1 h-5 rounded-md flex items-center px-2" style={{ background: "rgba(255,255,255,0.06)" }}>
                    {url && <span className="text-white/30 text-[10px] font-poppins select-none">{url}</span>}
                </div>
            </div>
            {children}
        </div>
    );
}

/** Closing call-to-action band. */
export function CtaBand({
    title,
    lede,
    primaryLabel,
    primaryHref,
    secondaryLabel,
    secondaryHref,
    decor,
}: {
    title: string;
    lede: string;
    primaryLabel: string;
    primaryHref: string;
    secondaryLabel?: string;
    secondaryHref?: string;
    decor?: ReactNode;
}) {
    return (
        <FeatureSection grid>
            {decor}
            <div className="flex flex-col items-center text-center gap-4">
                <h2 className="font-akira text-2xl sm:text-3xl text-white leading-snug max-w-2xl">{title}</h2>
                <p className="text-gray-500 font-poppins text-sm sm:text-base max-w-xl leading-relaxed">{lede}</p>
                <div className="flex flex-col sm:flex-row items-center gap-3 mt-2">
                    <a
                        href={primaryHref}
                        className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full font-semibold font-poppins text-white text-sm transition-all duration-300 hover:brightness-110"
                        style={{ background: "linear-gradient(90deg, #6416a3 0%, #7c3aed 100%)" }}
                    >
                        {primaryLabel}
                        <ChevronRight className="w-4 h-4" />
                    </a>
                    {secondaryLabel && secondaryHref && (
                        <a
                            href={secondaryHref}
                            target={secondaryHref.startsWith("http") ? "_blank" : undefined}
                            rel={secondaryHref.startsWith("http") ? "noopener noreferrer" : undefined}
                            className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full font-semibold font-poppins text-sm transition-colors hover:text-white text-purple-200 bg-primary/15 hover:bg-primary/25"
                        >
                            {secondaryLabel}
                        </a>
                    )}
                </div>
            </div>
        </FeatureSection>
    );
}

/** Page hero: blurred backdrop, checklist, CTAs, and a visual slot on the right. */
export function FeatureHero({
    eyebrow,
    titleLine1,
    titleLine2,
    lede,
    bullets,
    highlights,
    primaryLabel,
    primaryHref,
    secondaryLabel,
    secondaryHref,
    backdrop,
    visual,
    wash = "rgba(111,22,163,0.13)",
    decor,
}: {
    /** ReactNode so pages can put an icon in front of the tag text. */
    eyebrow?: ReactNode;
    titleLine1: string;
    titleLine2: string;
    lede: string;
    bullets?: string[];
    /** Icon-led blocks rendered under the CTAs, in place of `bullets`. */
    highlights?: { Icon: IconType; title: string; desc: string }[];
    primaryLabel: string;
    primaryHref: string;
    secondaryLabel?: string;
    secondaryHref?: string;
    backdrop: string;
    visual?: ReactNode;
    /** Colour laid over the backdrop image. */
    wash?: string;
    /** Extra decoration (e.g. a crossing character image) behind the content. */
    decor?: ReactNode;
}) {
    return (
        <div className="relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: `url('${backdrop}')`,
                        backgroundSize: "cover",
                        backgroundPosition: "center top",
                        filter: "blur(6px) brightness(0.45)",
                        transform: "scale(1.05)",
                    }}
                />
                <div className="absolute inset-0" style={{ background: wash }} />
                <div
                    className="absolute inset-0"
                    style={{ background: "radial-gradient(ellipse 90% 60% at 50% 110%, #000000 0%, transparent 90%)" }}
                />
                <div
                    className="absolute top-0 left-0 right-0 h-40"
                    style={{ background: "linear-gradient(to bottom, #000000, transparent)" }}
                />
                {decor}
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 pt-16 pb-20">
                <div className="flex flex-col lg:flex-row items-center gap-12">
                    <div className="flex flex-col gap-5 text-left flex-1">
                        <span className="font-poppins text-xs tracking-widest uppercase text-purple-300/80">
                            {eyebrow}
                        </span>
                        <h1 className="font-akira text-2xl sm:text-3xl xl:text-4xl text-white leading-tight tracking-wide">
                            {titleLine1}
                            <br />
                            <span
                                className="bg-clip-text text-transparent"
                                style={{ backgroundImage: "linear-gradient(90deg, #c084fc 0%, #a855f7 100%)" }}
                            >
                                {titleLine2}
                            </span>
                        </h1>
                        <p className="text-gray-300 font-poppins text-sm sm:text-base leading-relaxed max-w-xl">{lede}</p>
                        {bullets && (
                            <ul className="flex flex-col gap-2.5 mt-1">
                            {bullets.map((b) => (
                                <li key={b} className="flex items-center gap-3">
                                    <span
                                        className="flex-shrink-0 w-5 h-5 rounded-xl flex items-center justify-center"
                                        style={{ background: "#a855f7" }}
                                    >
                                        <svg className="w-3 h-3 text-black" viewBox="0 0 12 12" fill="none">
                                            <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                    <span className="text-gray-200 font-poppins text-sm sm:text-base">{b}</span>
                                </li>
                            ))}
                            </ul>
                        )}
                        <div className="flex flex-col sm:flex-row items-start gap-3 mt-3">
                            <a
                                href={primaryHref}
                                className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full font-semibold font-poppins text-white text-sm transition-all duration-300 hover:brightness-110"
                                style={{ background: "linear-gradient(90deg, #6416a3 0%, #7c3aed 100%)" }}
                            >
                                {primaryLabel}
                                <ChevronRight className="w-4 h-4" />
                            </a>
                            {secondaryLabel && secondaryHref && (
                                <a
                                    href={secondaryHref}
                                    target={secondaryHref.startsWith("http") ? "_blank" : undefined}
                                    rel={secondaryHref.startsWith("http") ? "noopener noreferrer" : undefined}
                                    className="inline-flex items-center gap-2 px-5 py-3.5 rounded-full font-semibold font-poppins text-sm transition-colors hover:text-white text-purple-200 bg-primary/15 hover:bg-primary/25"
                                >
                                    {secondaryLabel}
                                </a>
                            )}
                        </div>
                        {highlights && (
                            <div className="flex flex-col gap-6 mt-6">
                                {highlights.map(({ Icon, title, desc }) => (
                                    <div key={title} className="flex gap-3.5">
                                        <span
                                            className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
                                            style={{ background: "rgba(124,58,237,0.2)", border: "1px solid rgba(168,85,247,0.35)" }}
                                        >
                                            <Icon className="text-purple-300" size={15} />
                                        </span>
                                        <div>
                                            <p className="text-white font-poppins text-sm font-semibold">{title}</p>
                                            <p className="text-gray-400 font-poppins text-xs sm:text-sm leading-relaxed mt-1 max-w-md">
                                                {desc}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {visual && <div className="w-full lg:w-[46%] flex-shrink-0">{visual}</div>}
                </div>
            </div>
        </div>
    );
}
